import React, { useState } from "react";
import { EMPLOYEES_TO_EVALUATE, ALREADY_EVALUATED, EXECUTIVES } from "../../../../data/origami/employeeEvaluationData";

export default function EvaluationDashboard({ completedEmployees, onComplete, onClose }) {
  const [showResult, setShowResult] = useState(false);

  // Use completedEmployees from parent (passed as Set)
  const allCompleted = completedEmployees.size === EMPLOYEES_TO_EVALUATE.length;

  const handleFinalize = () => {
    if (allCompleted) {
      setShowResult(true);
    }
  };

  if (showResult) {
    return (
      <div className="py-12 px-6 bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-950 min-h-[80vh]">
        <div className="max-w-4xl mx-auto">
          {/* Success message */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">✓</div>
            <div className="text-2xl font-bold text-emerald-300 mb-2">
              Évaluations annuelles complétées
            </div>
            <div className="text-slate-400">
              {ALREADY_EVALUATED.length + EMPLOYEES_TO_EVALUATE.length}/{ALREADY_EVALUATED.length + EMPLOYEES_TO_EVALUATE.length + EXECUTIVES.length} employés traités
            </div>
          </div>

          {/* Executives section */}
          <div className="bg-slate-900/60 border border-amber-500/30 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="text-3xl">⚠️</div>
              <div>
                <div className="text-lg font-semibold text-amber-300 mb-2">
                  Employés restants (3)
                </div>
                <div className="text-sm text-slate-400 mb-4">
                  Les employés suivants ne peuvent pas être évalués via ce système.
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {EXECUTIVES.map(exec => (
                <div
                  key={exec.id}
                  className="flex items-center justify-between p-4 bg-slate-800/60 border border-slate-700 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold">
                      {exec.id === 1 ? '?' : exec.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {exec.id === 1 ? '[NOM MASQUÉ]' : exec.name}
                      </div>
                      <div className="text-sm text-slate-400">
                        {exec.poste} • ID #{exec.id}
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-xs font-semibold text-amber-300">
                    EXECUTIVE
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Explanation */}
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6 mb-6">
            <div className="text-sm text-indigo-300 font-semibold mb-3">
              📋 Statut EXECUTIVE
            </div>
            <div className="text-sm text-slate-300 space-y-2">
              <p>
                Les employés avec le statut <span className="font-semibold text-amber-300">EXECUTIVE</span> sont
                hors périmètre des évaluations annuelles standard.
              </p>
              <p>
                Ces postes de direction sont évalués directement par le conseil d'administration
                selon un processus distinct.
              </p>
              <p className="text-xs text-slate-500 mt-3">
                Classification ORIGAMI : Accès niveau Direction
              </p>
            </div>
          </div>

          {/* Action button */}
          <div className="text-center">
            <button
              onClick={() => {
                onComplete?.();
                onClose?.();
              }}
              className="px-8 py-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-lg transition shadow-lg"
            >
              Terminer et enregistrer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-6 bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-950 min-h-[80vh]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="text-2xl font-bold text-white mb-2">
            Système d'évaluation annuelle ORIGAMI
          </div>
          <div className="text-sm text-slate-400">
            Complétez les évaluations en attente pour finaliser la campagne RH
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="text-sm text-slate-400">
              Progression :
              <span className="text-white font-bold ml-2">
                {ALREADY_EVALUATED.length + completedEmployees.size} / {ALREADY_EVALUATED.length + EMPLOYEES_TO_EVALUATE.length}
              </span>
            </div>
            <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                style={{
                  width: `${((ALREADY_EVALUATED.length + completedEmployees.size) / (ALREADY_EVALUATED.length + EMPLOYEES_TO_EVALUATE.length)) * 100}%`
                }}
              />
            </div>
          </div>
        </div>

        {/* Employee lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Already evaluated */}
          <div>
            <div className="text-lg font-semibold text-emerald-300 mb-4">
              ✓ Évaluations validées ({ALREADY_EVALUATED.length + completedEmployees.size})
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {ALREADY_EVALUATED.map(emp => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-3 bg-slate-900/40 border border-emerald-700/30 rounded-lg opacity-60"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-700/40 flex items-center justify-center text-emerald-300 text-sm">
                      ✓
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-300">
                        {emp.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        ID #{emp.id}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-emerald-400">
                    Validé
                  </div>
                </div>
              ))}

              {/* Show completed evaluations from this session */}
              {EMPLOYEES_TO_EVALUATE.filter(e => completedEmployees.has(e.id)).map(emp => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-3 bg-slate-900/40 border border-emerald-700/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-700/40 flex items-center justify-center text-emerald-300 text-sm">
                      ✓
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-300">
                        {emp.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {emp.poste} • ID #{emp.id}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-emerald-400">
                    Validé
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* To evaluate */}
          <div>
            <div className="text-lg font-semibold text-amber-300 mb-4">
              ⏳ En attente d'évaluation ({EMPLOYEES_TO_EVALUATE.length - completedEmployees.size})
            </div>
            <div className="space-y-2">
              {EMPLOYEES_TO_EVALUATE.map(emp => {
                const isCompleted = completedEmployees.has(emp.id);
                return (
                  <div
                    key={emp.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border transition-all
                      ${isCompleted
                        ? 'bg-slate-900/40 border-emerald-700/30 opacity-60'
                        : 'bg-slate-900/60 border-slate-700 hover:border-indigo-500'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                        ${isCompleted
                          ? 'bg-emerald-700/40 text-emerald-300'
                          : 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white'
                        }
                      `}>
                        {isCompleted ? '✓' : emp.photo}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">
                          {emp.name}
                        </div>
                        <div className="text-xs text-slate-400">
                          {emp.poste} • ID #{emp.id}
                        </div>
                      </div>
                    </div>
                    {!isCompleted && (
                      <button
                        onClick={() => {
                          // This will be handled by parent component
                          const event = new CustomEvent('evaluateEmployee', { detail: emp });
                          window.dispatchEvent(event);
                        }}
                        className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition"
                      >
                        Évaluer →
                      </button>
                    )}
                    {isCompleted && (
                      <div className="text-xs text-emerald-400">
                        Validé
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Finalize button */}
        {allCompleted && (
          <div className="mt-8 text-center">
            <button
              onClick={handleFinalize}
              className="px-8 py-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-lg transition shadow-lg animate-pulse"
            >
              Finaliser les évaluations →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}